import React, { Dispatch, SetStateAction, useState } from 'react';

import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import NwLandGraph from './LandNwGraph';
import { ChartData, KdTick, KdTicks } from '../../@types/KingdomResponse';
import { Tick } from '../../@types/State';
import { useIntelState } from '../../context/IntelState';
import { useTicks } from '../../hooks/useTicks';
import RestUtilities from '../../services/RestUtilities';

const getMonthMenuItems = (months: string[]) => {
  return months.map((m: string) => (
    <MenuItem key={m} value={m}>
      {m}
    </MenuItem>
  ));
};

const getValuesFromTick = (currentTick: Tick | undefined) => {
  let year = 0;
  let month = 'January';
  let day = 1;
  if (currentTick) {
    const dateParts = currentTick.tickName.split(' ');
    if (dateParts.length < 3) return { year: 1, month: 1, day: 1 };
    month = dateParts[0];
    day = Number(dateParts[1].replace(/,\s*$/, ''));
    year = Number(dateParts[2].replace('YR', ''));
  }
  return { year, month, day };
};

const KdGraphs: React.FC = () => {
  const { intelState } = useIntelState();

  const { year, month, day } = getValuesFromTick(intelState.currentTick);
  const [dataSet, setDataSet] = useState<ChartData[] | undefined>(undefined);
  const [fetching, setFetching] = useState(false);
  const [kd1, setKd1] = useState(intelState.ownData?.location ?? '1:1');
  const [kd2, setKd2] = useState(intelState.enemyData?.location ?? '');

  const [numberMonths, setNumberMonths] = useState(3);
  const [dayEnd, setDayEnd] = useState(day);
  const [monthEnd, setMonthEnd] = useState(month);
  const [yearEnd, setYearEnd] = useState(year);
  const { ticks, days, months, years } = useTicks();
  const validateKeyPress = (
    e: any,
    action: Dispatch<SetStateAction<string>>
  ): void => {
    const regex = /[0-9:]/gm;
    if (e.key === 'Enter') {
      action(e.target.value);
      e.preventDefault();
    } else if (!regex.exec(e.key)) {
      e.preventDefault();
    }
  };

  const refreshData = () => {
    const getData = async (tickId: number | undefined) => {
      const numberTicks = numberMonths * 24;
      const response = await RestUtilities.post(`Kingdom/v1/Ticks`, {
        server: intelState.server,
        numberTicks: numberTicks,
        locations: [kd1, kd2],
        tickId: tickId ?? 1,
      });
      if (response.ok) {
        const ticks: KdTicks = response.content;
        const kd1Ticks: KdTick[] = ticks[kd1];
        const kd2Ticks: KdTick[] | null = kd2 ? ticks[kd2] : null;
        const newDataSet: ChartData[] = [];
        for (let index = 0; index < numberTicks; index++) {
          const tick1: KdTick = kd1Ticks[index];
          const tick2: KdTick | null = kd2Ticks ? kd2Ticks[index] : null;
          if (tick1) {
            newDataSet.push({
              tick: `${index + 1}`,
              kd1Land: tick1.land,
              kd1Nw: tick1.netWorth,
              kd1Honor: tick1.honor,
              kd2Land: tick2?.land,
              kd2Nw: tick2?.netWorth,
              kd2Honor: tick2?.honor,
            } as ChartData);
          }
        }
        setDataSet(newDataSet);
        setFetching(false);
      }
    };
    if (!fetching) {
      setFetching(true);
      const tickName = `${monthEnd} ${dayEnd}, YR${yearEnd}`;
      const tick = ticks?.find((x: Tick) => x.tickName === tickName);
      if (tick) {
        getData(tick.tickNumber - numberMonths * 24);
      }
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
      }}
    >
      <Typography variant='h4' component='div'>
        Networth and Land
      </Typography>
      <Grid container spacing={2} mt={2} columns={8}>
        <Grid>
          <TextField
            id='kd1-switcher'
            label='Kd1'
            value={kd1}
            variant='outlined'
            size='small'
            sx={{ width: '100px', marginRight: '10px' }}
            onChange={(event) => setKd1(event.target.value)}
            onKeyPress={(e) => validateKeyPress(e, setKd1)}
          />
        </Grid>
        <Grid>
          <TextField
            id='kd2-switcher'
            label='Kd2'
            value={kd2}
            variant='outlined'
            size='small'
            sx={{ width: '100px', marginRight: '10px' }}
            onChange={(event) => setKd2(event.target.value)}
            onKeyPress={(e) => validateKeyPress(e, setKd2)}
          />
        </Grid>
        {ticks && (
          <>
            <Grid>
              <TextField
                id='dayEnd'
                variant='outlined'
                fullWidth
                size='small'
                type='number'
                label={'Day End'}
                sx={{ minWidth: '100px' }}
                slotProps={{
                  input: {
                    inputProps: {
                      min: 1,
                      max: days,
                    },
                  },
                }}
                value={dayEnd}
                onChange={(e) => setDayEnd(Number(e.target.value))}
              />
            </Grid>
            <Grid>
              <FormControl variant='outlined' fullWidth size='small'>
                <InputLabel id='month-label'>Month End</InputLabel>
                <Select
                  variant='outlined'
                  id='month-select'
                  name='month-select'
                  labelId='month-label'
                  value={monthEnd}
                  label='Month End'
                  onChange={(e) => {
                    setMonthEnd(e.target.value);
                  }}
                  sx={{ minWidth: '120px' }}
                >
                  {getMonthMenuItems(months)}
                </Select>
              </FormControl>
            </Grid>
            <Grid>
              <TextField
                variant='outlined'
                fullWidth
                id='yearEnd'
                size='small'
                type='number'
                label={'Year End'}
                sx={{ minWidth: '100px' }}
                slotProps={{
                  input: {
                    inputProps: {
                      min: 0,
                      max: years,
                    },
                  },
                }}
                value={yearEnd}
                onChange={(e) => setYearEnd(Number(e.target.value))}
              />
            </Grid>
          </>
        )}
        <Grid>
          <TextField
            variant='outlined'
            fullWidth
            id='durationMonths'
            size='small'
            type='number'
            label={'Duration Months'}
            sx={{ minWidth: '120px' }}
            slotProps={{
              input: {
                inputProps: {
                  min: 0,
                  max: 12,
                },
              },
            }}
            value={numberMonths}
            onChange={(e) => setNumberMonths(parseInt(e.target.value))}
          />
        </Grid>

        <Grid>
          <Button variant='outlined' onClick={refreshData}>
            Get Data
          </Button>
        </Grid>
      </Grid>
      {dataSet && (
        <NwLandGraph
          dataSet={dataSet}
          hasMultiple={!!kd2}
          kd1={kd1 ?? ''}
          kd2={kd2 ?? ''}
        />
      )}
    </Box>
  );
};

export default KdGraphs;
